import { PCBBackdrop } from "@components/PCBBackdrop";
import { useChatSurface } from "@layouts/AppLayout/useLayoutMode";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Box, Button, Chip, CircularProgress, Stack, Typography, alpha } from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useCallback, useRef } from "react";
import { useChat } from "../hooks/useChat";
import { ChatComposerArea } from "./ChatComposerArea";
import type { ChatInputHandle } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";
import {
  ChatScrollAnchor,
  type ChatScrollAnchorHandle,
} from "./ChatScrollAnchor";
import { ChatSkeleton } from "./ChatSkeleton";
import { StreamingMessage } from "./StreamingMessage";
import { WelcomeScreen } from "./WelcomeScreen";

/**
 * Conversation core — the bit shared between the full-screen `/chat`
 * page (`ChatContainer`) and the persistent right-rail Copilot
 * (`CopilotRail`). Owns the message list, streaming bubble, and
 * composer. Does *not* render page chrome — the wrapping component is
 * responsible for any header / history panel / outer layout.
 *
 * The variant flag tunes density + backdrop:
 *   - `page` keeps the spacious /chat layout with the PCB-trace
 *     watermark behind the scroll region.
 *   - `rail` drops the backdrop (would look noisy at 380px) and
 *     tightens horizontal padding to fit the narrower column.
 *
 * Both variants hit the same `useChat` hook → same `chatStore` →
 * therefore the same conversation. The hook is only ever mounted in
 * one place at a time because the rail is hidden on chat surfaces; the
 * store is the single source of truth either way.
 */
interface ChatConversationProps {
  variant: "page" | "rail";
  isHydrating?: boolean;
  /** Optional slot rendered between the message scroll area and the
   *  composer. Previously hosted the ActiveToolsChipStrip; retained
   *  for any surface-specific affordances. */
  aboveComposer?: React.ReactNode;
}

export function ChatConversation({
  variant,
  isHydrating = false,
  aboveComposer,
}: ChatConversationProps) {
  const {
    messages,
    sessionId,
    isStreaming,
    streamingContent,
    activity,
    transientStatus,
    hasMoreOlder,
    isLoadingOlder,
    isHydrating: isHydratingStore,
    sendMessage,
    stopStreaming,
    loadOlderMessages,
  } = useChat();

  // Combine the URL-hydration prop (driven by ChatPage's mount-time
  // ?s=<sid> sync) with the sidebar-driven store flag, so the skeleton
  // surfaces during both flows. Either source flips it on; both clear
  // it via their own teardown.
  const showSkeleton =
    (isHydrating || isHydratingStore) && messages.length === 0;

  // Mid-turn artifacts. Selected directly because they're not part of
  // the public useChat surface (no actions on them — pure rendering).
  const liveArtifacts = useChatStore((s) => s.liveArtifacts);
  const isReconnecting = useChatStore((s) => s.isReconnecting);

  const inputRef = useRef<ChatInputHandle>(null);
  const scrollAnchorRef = useRef<ChatScrollAnchorHandle>(null);

  const handleLoadOlder = useCallback(() => {
    if (isLoadingOlder) return;
    scrollAnchorRef.current?.captureForPrepend();
    scrollAnchorRef.current?.suppressNextUnseen();
    void loadOlderMessages();
  }, [loadOlderMessages, isLoadingOlder]);

  const handleSend = (text: string) => {
    void sendMessage(text);
  };

  const isRail = variant === "rail";
  const contentRow = { width: "100%" } as const;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, position: "relative", minHeight: 0 }}>
        {!isRail && messages.length > 0 && !showSkeleton && (
          <PCBBackdrop
            variant="chat"
            tile={120}
            intensity={0.55}
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          />
        )}
        <ChatScrollAnchor
          ref={scrollAnchorRef}
          messagesLength={messages.length}
          streamingContent={streamingContent}
          isStreaming={isStreaming}
          sessionId={sessionId}
        >
          <Box
            sx={{
              ...contentRow,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              py: isRail ? 0.5 : 1,
            }}
          >
            {showSkeleton ? (
              <ChatSkeleton />
            ) : messages.length === 0 && !isStreaming ? (
              isRail ? (
                <RailEmptyState onSuggestionSelect={handleSend} />
              ) : (
                <WelcomeScreen onSuggestionSelect={handleSend} />
              )
            ) : (
              <>
                {hasMoreOlder && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 1,
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleLoadOlder}
                      disabled={isLoadingOlder}
                      startIcon={
                        isLoadingOlder ? <CircularProgress size={12} /> : null
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    >
                      {isLoadingOlder ? "Loading…" : "Load older messages"}
                    </Button>
                  </Box>
                )}
                {(() => {
                  let latestAssistantIdx = -1;
                  for (let i = messages.length - 1; i >= 0; i--) {
                    if (messages[i]?.role === "assistant") {
                      latestAssistantIdx = i;
                      break;
                    }
                  }
                  // Loadout-change markers were retired with the workbench
                  // tool picker (May 2026); the conversation is now a
                  // straight list of messages, no inline tool-loadout
                  // breaks.
                  return messages.map((msg, idx) => (
                    <ChatMessage
                      key={msg.id ?? idx}
                      message={msg}
                      compactIteration={
                        msg.role === "assistant" && idx !== latestAssistantIdx
                      }
                      isLatestAssistant={
                        msg.role === "assistant" && idx === latestAssistantIdx
                      }
                      onSendFollowUp={handleSend}
                      isStreaming={isStreaming}
                    />
                  ));
                })()}
                {isStreaming &&
                  (streamingContent ||
                    activity.length > 0 ||
                    transientStatus) && (
                    <StreamingMessage
                      content={streamingContent}
                      activity={activity}
                      transientStatus={transientStatus}
                      liveArtifacts={liveArtifacts}
                    />
                  )}
              </>
            )}
          </Box>
        </ChatScrollAnchor>
      </Box>

      {aboveComposer}

      <ChatComposerArea
        inputRef={inputRef}
        onSend={handleSend}
        onStop={stopStreaming}
        isStreaming={isStreaming}
        isReconnecting={isReconnecting}
        contentRow={contentRow}
      />
    </Box>
  );
}

/**
 * Quiet empty state for the rail. Surface-aware: on Workbench it's a
 * one-liner; on Requirements it's a short prompt + suggestion chips
 * that kick the requirements assistant straight into the iteration
 * loop without the user typing anything from a cold start.
 */
function RailEmptyState({
  onSuggestionSelect,
}: {
  onSuggestionSelect: (text: string) => void;
}) {
  const surface = useChatSurface();

  if (surface === "requirements") {
    const suggestions = [
      "Tell me about the device — what is it and who's it for?",
      "What's missing for Product tier?",
      "Walk me through Power requirements",
      "Convert all my drafts to shall-form",
    ];
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 2,
          py: 3,
          gap: 1.75,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon
            sx={(t) => ({
              fontSize: 16,
              color: t.palette.editorial?.main ?? t.palette.warning.main,
            })}
          />
          <Typography
            variant="overline"
            sx={(t) => ({
              fontSize: "0.65rem",
              fontWeight: 600,
              letterSpacing: "0.16em",
              color: t.palette.editorial?.contrastText ?? t.palette.text.secondary,
              lineHeight: 1.2,
            })}
          >
            Requirements assistant
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.55,
            color: "text.primary",
          }}
        >
          I'll draft requirements as we talk — you accept each one before it
          lands. Start with a sentence about the device, or pick a prompt:
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 0.5 }}
        >
          {suggestions.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              onClick={() => onSuggestionSelect(s)}
              sx={(t) => ({
                fontSize: "0.75rem",
                height: 26,
                px: 0.5,
                borderRadius: 1,
                background: alpha(
                  t.palette.editorial?.main ?? t.palette.warning.main,
                  0.06,
                ),
                border: `1px solid ${alpha(
                  t.palette.editorial?.main ?? t.palette.warning.main,
                  0.32,
                )}`,
                color: "text.primary",
                cursor: "pointer",
                "&:hover": {
                  background: alpha(
                    t.palette.editorial?.main ?? t.palette.warning.main,
                    0.14,
                  ),
                },
              })}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // Workbench (and any other Copilot surface) — quiet one-liner.
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        textAlign: "center",
        color: "text.secondary",
        fontSize: "0.8125rem",
        lineHeight: 1.55,
      }}
    >
      Ask Copilot anything about this project — it has the same context as the
      full chat.
    </Box>
  );
}
